import { call, put, takeEvery, takeLeading } from 'redux-saga/effects';
import {
    FETCH_USER,
    LOGIN_USER,
    LOGOUT_USER,
    SET_USER,
    UPDATE_REMOTE_USER,
    UPDATE_USER,
    CALL_LOGOUT_USER,
    UPDATE_AUTH_USER_PASSCODE,
} from 'store/key';
import { produce } from 'utility/general';
import api from 'api/call';
import { KEYS, setItem } from 'storage';
import moment from 'moment';
import { setGlobalLoading } from 'store/global/reducer';
import { safeSaga } from 'store';

const initialState = {
    isLogin: false,
    role: 'applicant',
    email: '',
    isLoading: false,
    isError: false,
    shouldUpdatePassword: false,
    shouldUpdateToken: null,
    detail: {},
    roles: [],
};

function* updateAuthUserPassword(action) {
    const { newPassword, oldPassword, callback = () => null } = action.payload;

    try {
        yield put({
            type: SET_USER,
            payload: { isLoading: true },
        });

        // if password update success, status code will be 200
        yield call(api.user.changeAuthUserPassword, { oldPassword, newPassword });

        yield put({
            type: SET_USER,
            payload: { isLoading: false },
        });

        callback();
    } catch (error) {
        console.log('144704 reducer.js error', error);
        yield put({
            type: SET_USER,
            payload: { isLoading: false },
        });
    }
}

function* updateRemoteUser(action) {
    const postData = action.payload;

    try {
        yield put({
            type: SET_USER,
            payload: { isLoading: true },
        });

        const res = yield call(api.user.updateUserInfo, postData);
        const updatedUser = res.data.result;
        yield put({
            type: SET_USER,
            payload: { detail: updatedUser, isLoading: false },
        });

        if (action?.payload?.callback) {
            action?.payload?.callback();
        }
    } catch (error) {
        console.log('144704 reducer.js error', error);
        yield put({
            type: SET_USER,
            payload: { isLoading: false },
        });
    }
}

function* fetchUser() {
    try {
        const res = yield call(api.user.getUserInfo);
        const { user, roles } = res.data.result;
        yield put({
            type: SET_USER,
            payload: {
                detail: user,
                roles,
            },
        });
    } catch (error) {
        console.log('170328 reducer.js fetch user failed', error);
    }
}

function* updateUser(action) {
    const {
        shouldUpdatePassword,
        role,
        userEmail,
        accessToken,
        refreshToken,
        isUserRemembered,
        lastLogin,
        isLoading,
        isError,
        isLogout,
        shouldUpdateToken,
        isLogin,
        callback = () => null,
    } = action.payload;
    // if isLogout === true, all data will be cleared in both localStorage and redux store

    // setting data (will be set back when browser refresh) to the local storage,
    setUserLocalStorage(
        {
            role,
            userEmail,
            accessToken,
            refreshToken,
            lastLogin,
            isUserRemembered,
            isLogin,
        },
        isLogout,
    );

    let updatedObject = {};

    if (!isLogout) {
        if (isLogin !== undefined) {
            updatedObject.isLogin = isLogin;
        }

        if (lastLogin !== undefined) {
            updatedObject.lastLogin = lastLogin;
        }

        if (role !== undefined) {
            updatedObject.role = role;
        }

        if (userEmail !== undefined) {
            updatedObject.email = userEmail;
        }

        if (isLoading !== undefined) {
            updatedObject.isLoading = isLoading;
        }

        if (isError !== undefined) {
            updatedObject.isError = isError;
        }

        if (isLoading !== undefined) {
            updatedObject.isLoading = isError;
        }

        if (shouldUpdatePassword !== undefined) {
            updatedObject.shouldUpdatePassword = shouldUpdatePassword;
        }

        if (shouldUpdateToken !== undefined) {
            updatedObject.shouldUpdateToken = shouldUpdateToken;
        }
    } else {
        updatedObject = {
            isLogin: false,
            email: '',
            role: '',
        };
    }

    yield put({
        type: SET_USER,
        payload: updatedObject,
    });

    callback();
}

function* logoutUser(action) {
    const callback = action?.payload?.callback;

    setUserLocalStorage({ isLogout: true });

    yield put({
        type: UPDATE_USER,
        payload: { isLogout: true },
    });

    callback?.();
}

function* callLogoutUser(action) {
    const callback = action?.payload?.callback;
    const final = action?.payload?.final;
    try {
        yield call(api.user.logoutUser, {});
        callback?.();
    } catch (error) {
        console.log('logout user failed', error);
    }

    final?.();
}

const setUserLocalStorage = async (
    { role, userEmail, accessToken, refreshToken, isUserRemembered, lastLogin, isLogin },
    isLogout = false,
) => {
    if (isLogout) {
        // data will be cleared when logout action
        setItem(KEYS.USER_ROLE, '');
        setItem(KEYS.USER_EMAIL, '');
        // setItem(KEYS.USER_REMEMBER, false);
        setItem(KEYS.ACCESS_TOKEN, '');
        setItem(KEYS.REFRESH_TOKEN, '');
        setItem(KEYS.USER_LAST_LOGIN, '');
        setItem(KEYS.USER_IS_LOGIN, false);
        return;
    }

    if (role !== undefined) {
        setItem(KEYS.USER_ROLE, role);
    }

    if (userEmail !== undefined) {
        setItem(KEYS.USER_EMAIL, userEmail);
    }

    if (isUserRemembered !== undefined) {
        setItem(KEYS.USER_REMEMBER, isUserRemembered);
    }

    if (accessToken !== undefined) {
        setItem(KEYS.ACCESS_TOKEN, accessToken);
    }

    if (refreshToken !== undefined) {
        setItem(KEYS.REFRESH_TOKEN, refreshToken);
    }

    if (lastLogin !== undefined) {
        setItem(KEYS.USER_LAST_LOGIN, lastLogin);
    }

    if (isLogin !== undefined) {
        setItem(KEYS.USER_IS_LOGIN, isLogin);
    }
};

function* loginUser(action) {
    const { username, password, role, rememberChecked = false, callback = () => {} } = action.payload;

    try {
        setGlobalLoading(true);
        yield put({ type: UPDATE_USER, payload: { isLoading: true, isError: false } });
        const res = yield call(api.user.login, { username, password, role });
        const { token, refreshToken, shouldUpdatePassword, shouldUpdateToken } = res.data.result;

        if (rememberChecked) {
            setItem(KEYS.REMEMBER_EMAIL, username);
            setItem(KEYS.REMEMBER_ROLE, role);
        }

        const firstLog = shouldUpdatePassword;

        const userInfo = {
            isLogin: true,
            email: username,
            role,
            lastLogin: moment().format('YYYY-MM-DD HH:mm:ss'),
        };

        if (firstLog) {
            setItem(KEYS.SHOULD_UPDATE_TOKEN, shouldUpdateToken);
            yield put({
                type: UPDATE_USER,
                payload: {
                    isLogin: false,
                    shouldUpdatePassword: true,
                    role,
                    refreshToken: refreshToken,
                    accessToken: token,
                },
            });
        } else {
            //update global state isLogin in user reducer
            yield put({
                type: UPDATE_USER,
                payload: {
                    ...userInfo,
                    accessToken: token,
                    isUserRemembered: rememberChecked,
                    refreshToken: refreshToken,
                    isLoading: false,
                    callback: () => {
                        callback();
                    },
                },
            });
        }
    } catch (error) {
        console.log('113344 reducer.js error', error);
        yield put({
            type: UPDATE_USER,
            payload: { isError: true, isLoading: false, isLogout: true },
        });
    }

    setGlobalLoading(false);
}

const reducer = (state = initialState, action) => {
    switch (action.type) {
        case SET_USER:
            const newState = produce(state, (draft) => {
                Object.entries(action.payload).forEach(([key, value]) => {
                    draft[key] = action.payload[key];
                });
            });
            return newState;
        default:
            return state;
    }
};

export function* watchUser() {
    yield takeEvery(UPDATE_USER, safeSaga(updateUser));
    yield takeLeading(LOGIN_USER, safeSaga(loginUser));
    yield takeLeading(LOGOUT_USER, safeSaga(logoutUser));
    yield takeLeading(FETCH_USER, safeSaga(fetchUser));
    yield takeLeading(UPDATE_REMOTE_USER, safeSaga(updateRemoteUser));
    yield takeLeading(UPDATE_AUTH_USER_PASSCODE, safeSaga(updateAuthUserPassword));
    yield takeLeading(CALL_LOGOUT_USER, safeSaga(callLogoutUser));
}

export default reducer;
